// components/WorkspaceTimelinePanel.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { normalizeVideoDimension, toFinite, type TimelineCompositionStatus } from '#lib/timeline-video-types';
import { type GenerationTask, type QuickMenuAction } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { projectSurfaceOf } from '#lib/modelist/projectScene';
import { STUDIO_LIVE_VIEWPORT_VARS_RESET } from '#lib/studio-viewport';
import { uploadFileToCloud } from '#lib/azure-client';
import { extractAudioFromVideo } from '#lib/audio-extract-client';
import type { PendingVideoGenerationTrack } from '#lib/timeline-generation-placeholders';
import {
  TIMELINE_AUDIO_GAIN_PARAM_KEY,
  MIN_TIMELINE_TRACK_SPAN_MS,
  TIMELINE_AUDIO_ONLY_PARAM_KEY,
  TIMELINE_MUTE_AUDIO_PARAM_KEY,
  TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY,
  TIMELINE_REPLACED_BY_EDIT_PARAM_KEY,
  buildWorkspacePendingGenerationSignature,
  readTimelineParamFlag,
  buildWorkspaceTimelineSummarySignature,
  parseWorkspacePendingGenerationSignature,
} from '#lib/workspace-timeline';
import SimpleLoader from '@/components/ui/SimpleLoader';
import { WorkspaceTimelineCursorOverlay } from './workspace-timeline/WorkspaceTimelineCursorOverlay';
import { WorkspaceTimelineInsertLanePreview } from './workspace-timeline/WorkspaceTimelineInsertLanePreview';
import { WorkspaceTimelineLaneList } from './workspace-timeline/WorkspaceTimelineLaneList';
import { WorkspaceTimelinePendingTracks } from './workspace-timeline/WorkspaceTimelinePendingTracks';
import { WorkspaceTimelinePlaybackControls } from './workspace-timeline/WorkspaceTimelinePlaybackControls';
import { WorkspaceTimelineRuler } from './workspace-timeline/WorkspaceTimelineRuler';
import { useWorkspaceTimelineInteractionController } from './workspace-timeline/useWorkspaceTimelineInteractionController';
import { useWorkspaceTimelinePlaybackController } from './workspace-timeline/useWorkspaceTimelinePlaybackController';
import { useWorkspaceTimelineVolumeController } from './workspace-timeline/useWorkspaceTimelineVolumeController';
import { useWorkspaceTimelineContextMenuController } from './workspace-timeline/useWorkspaceTimelineContextMenuController';
import { useWorkspaceTimelineTrackProjection } from './workspace-timeline/useWorkspaceTimelineTrackProjection';
import { waitForTimelineExportReadyViaSocket } from './workspace-timeline/useTimelineExportRealtimeStatus';
import {
  clamp,
  cloneTimelineValue,
  getNormalizedAudioGain,
  getWorkspaceTimelineTrackSource as getTrackSource,
  mergeTimelineAudioParams,
} from './workspace-timeline/presentation';
import type {
  ResolvedWorkspaceTimelineTrack,
  TimelineFileDropPreview,
  TimelineLaneLayoutPresentation,
  TimelineLaneRow,
  TimelineLaneRowActions,
  TimelineRangePresentation,
  TimelineToolState,
  TrackDragTimingPreview,
  TrackLaneDragPreview,
  WorkspaceComposedPlaybackState,
  WorkspacePlaybackMediaClockHint,
  WorkspaceTimelineTrack,
} from './workspace-timeline/types';
export type {
  WorkspaceComposedPlaybackState,
  WorkspacePlaybackMediaClockHint,
  WorkspaceTimelineTrack,
} from './workspace-timeline/types';

interface WorkspaceTimelinePanelProps {
  projectId: string;
  workspaceId: string;
  isActive: boolean;
  composedPlaybackVideoRef?: React.RefObject<HTMLVideoElement | null>;
  playbackMediaClockHintRef?: React.MutableRefObject<WorkspacePlaybackMediaClockHint>;
  onComposedPlaybackStateChange?: (state: WorkspaceComposedPlaybackState) => void;
}

const MIN_TRACK_SPAN_MS = MIN_TIMELINE_TRACK_SPAN_MS;
const AUDIO_SPLIT_REQUEST_TIMEOUT_MS = 4 * 60 * 1000;
const EMPTY_GENERATION_TASKS: GenerationTask[] = [];
const VIDEO_INPUT_TIMELINE_MODELS = new Set(['kling-3-omni', 'seedance-2.0', 'seedance-2.5', 'gemini-omni-flash-preview', 'topaz-video-upscale', 'mirelo-sfx-1.6', 'magnific-video-upscale']);
const TIMELINE_PLAYBACK_RATE_OPTIONS = [0.25, 0.5, 1, 2, 4] as const;
const TIMELINE_PLAYBACK_RATE_EPSILON = 0.001;
const LANE_LEFT_COLUMN_WIDTH_PX = 148;
const TIMELINE_GRID_GAP_PX = 8;
const TIMELINE_PORTAL_ROOT_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
};
// Docked to the canvas viewport, above the bottom pills (bottom-2 + 36px island + 8px gap) and clear
// of the left tool rail (12px + 42px), so pan and zoom never move, shrink or hide it.
const TIMELINE_DOCK_STYLE: React.CSSProperties = {
  ...STUDIO_LIVE_VIEWPORT_VARS_RESET,
  position: 'absolute',
  left: '64px',
  bottom: '52px',
  zIndex: 15,
  flexDirection: 'column',
  backgroundColor: '#1B1B1B',
  border: '1px solid #262626',
  boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
  right: '24px',
  maxHeight: '44%',
  transformOrigin: 'bottom left',
};
// Rises from the toolbar's timeline button into focus, sinks back on close; visibility flips after the fade.
const TIMELINE_DOCK_OPEN_STYLE: React.CSSProperties = {
  opacity: 1,
  transform: 'none',
  filter: 'none',
  visibility: 'visible',
  pointerEvents: 'auto',
  transition: 'opacity 160ms ease-out, transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1), filter 160ms ease-out, visibility 0s linear 0s, border-radius 160ms ease-out',
};
const TIMELINE_DOCK_CLOSED_STYLE: React.CSSProperties = {
  opacity: 0,
  transform: 'translateY(12px) scale(0.98)',
  filter: 'blur(4px)',
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'opacity 140ms ease-out, transform 140ms ease-out, filter 140ms ease-out, visibility 0s linear 140ms',
};
// With tracks: a card. Empty: one row, its radius concentric with the 32px drop pill inside (16 + 6).
const TIMELINE_DOCK_FILLED_STYLE: React.CSSProperties = { borderRadius: '16px', padding: '6px 12px 12px' };
const TIMELINE_DOCK_EMPTY_STYLE: React.CSSProperties = { borderRadius: '22px', padding: '6px' };
// No visible scrollbar: it would narrow the lanes and break their alignment with the ruler and playhead.
const DOCK_OPEN_FIT_WINDOW_MS = 5000;
const TIMELINE_LANES_SCROLL_STYLE: React.CSSProperties = { overflowY: 'auto', overflowX: 'hidden', minHeight: 0, scrollbarWidth: 'none' };
const TIMELINE_IMPORT_ROW_STYLE: React.CSSProperties = { height: '28px', fontSize: 13, color: '#8E8E8E' };
const TIMELINE_IMPORT_ROW_LABEL_STYLE: React.CSSProperties = { fontSize: 'inherit' };
const TIMELINE_RULER_GRID_STYLE: React.CSSProperties = {
  gridTemplateColumns: `${LANE_LEFT_COLUMN_WIDTH_PX}px minmax(0,1fr)`,
  columnGap: `${TIMELINE_GRID_GAP_PX}px`,
};
const TIMELINE_LANE_LAYOUT_PRESENTATION: TimelineLaneLayoutPresentation = {
  laneLeftColumnWidthPx: LANE_LEFT_COLUMN_WIDTH_PX,
};

const formatTimelinePlaybackRateOption = (rate: number): string => {
  const rounded = Math.round(rate * 100) / 100;
  if (Math.abs(rounded - Math.round(rounded)) < TIMELINE_PLAYBACK_RATE_EPSILON) {
    return `x${Math.round(rounded)}`;
  }
  return `x${rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}`;
};

const WorkspaceTimelinePanel = React.memo(({
  projectId,
  workspaceId,
  isActive,
  composedPlaybackVideoRef,
  playbackMediaClockHintRef,
  onComposedPlaybackStateChange,
}: WorkspaceTimelinePanelProps) => {
  const { kernel } = useKernel();
  // Kept mounted under its project's 3D scene, the timeline is on screen for nobody: the window's keys are the
  // scene's, Space must not start it there (ProjectPage pauses one playing at the switch).
  const onScreen = useKernelStore((state) => isActive && projectSurfaceOf(state, projectId) !== 'scene');
  const [mediaDurationVersion, setMediaDurationVersion] = useState(0);
  const [activeDragTrackId, setActiveDragTrackId] = useState<string | null>(null);
  const [activeTrackDragInitialLaneId, setActiveTrackDragInitialLaneId] = useState<string | null>(null);
  const [activeTrackOrderDragId, setActiveTrackOrderDragId] = useState<string | null>(null);
  const [separatingSoundTrackIds, setSeparatingSoundTrackIds] = useState<string[]>([]);
  const [mergingTrackIds, setMergingTrackIds] = useState<string[]>([]);
  const [splitHoverTrackId, setSplitHoverTrackId] = useState<string | null>(null);
  const [splitPreview, setSplitPreview] = useState<{ trackId: string; splitMs: number } | null>(null);
  const [dragSnapGuideMs, setDragSnapGuideMs] = useState<number | null>(null);
  const [trackDragTimingPreview, setTrackDragTimingPreview] = useState<TrackDragTimingPreview | null>(null);
  const [trackLaneDragPreview, setTrackLaneDragPreview] = useState<TrackLaneDragPreview | null>(null);
  const [timelineFileDropPreview, setTimelineFileDropPreview] = useState<TimelineFileDropPreview | null>(null);
  const [splitScissorPulseFrame, setSplitScissorPulseFrame] = useState(false);
  const probedTrackDurationsRef = useRef<Map<string, { src: string; durationMs: number }>>(new Map());
  const pendingDurationProbeKeysRef = useRef<Set<string>>(new Set());
  const lastAutoDurationWriteRef = useRef<number | null>(null);
  const resolvedTracksRef = useRef<ResolvedWorkspaceTimelineTrack[]>([]);
  const laneRowsRef = useRef<TimelineLaneRow[]>([]);
  const selectedLayerIdSetRef = useRef<Set<string>>(new Set());
  const displayTrackOrderLayerIdsRef = useRef<string[]>([]);
  const maxLaneOrderRef = useRef<number>(0);
  const rulerRangeStartMsRef = useRef<number>(0);
  const rulerRangeEndMsRef = useRef<number>(0);
  const rulerRangeMsRef = useRef<number>(1);
  const splitTrackAtMsRef = useRef<(track: ResolvedWorkspaceTimelineTrack, splitCursorMsRaw: number) => void>(() => {});
  const separateTrackAudioRef = useRef<(track: ResolvedWorkspaceTimelineTrack) => Promise<void>>(async () => {});
  const separatingSoundTrackLockRef = useRef<Set<string>>(new Set());
  const mergingTrackLockRef = useRef<Set<string>>(new Set());
  const mergeAbortControllersRef = useRef<Set<AbortController>>(new Set());
  const isMountedRef = useRef(false);
  const [showEndInsertLaneWhileDragging, setShowEndInsertLaneWhileDragging] = useState(false);
  const workspaceFrameW = useKernelStore((state) => {
    const value = state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect?.w;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  });
  const workspaceFrameH = useKernelStore((state) => {
    const value = state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect?.h;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  });
  const workspaceTimelineDurationMs = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.timelineDurationMs ?? null
  );
  const workspaceTimelineFps = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.timelineFps ?? null
  );
  const workspacePreferVersionPanelOverTimeline = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.preferVersionPanelOverTimeline === true
  );
  const workspaceImageTimelineManuallyEnabled = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.imageTimelineManuallyEnabled === true
  );
  const cursorMs = useKernelStore((state) => {
    const cursorValue = state.inventory.StudioWorkspace.registry[workspaceId]?.timelineCursorMs;
    return Math.max(0, Math.round(toFinite(cursorValue) ?? 0));
  });
  const onCursorChange = useCallback((nextCursorMs: number) => {
    kernel.current?.setWorkspaceTimelineCursor(projectId, workspaceId, nextCursorMs, { minDeltaMs: 16 });
  }, [kernel, projectId, workspaceId]);
  const activeTool = useKernelStore((state) => state.activeTool);
  const activeSoundTool = useKernelStore((state) => state.activeSoundTool);
  const diffusionModel = useKernelStore((state) => state.diffusionModel);
  const activeModelParams = useKernelStore((state) => state.modelParams[state.diffusionModel] ?? null);
  const generationTasks = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    return Array.isArray(runtime?.generationTasks) ? runtime.generationTasks : EMPTY_GENERATION_TASKS;
  });
  const pendingGenerationSignature = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    return buildWorkspacePendingGenerationSignature(runtime?.generationTasks, workspaceId);
  });
  const pendingVideoGenerationTracks = useMemo<PendingVideoGenerationTrack[]>(
    () => parseWorkspacePendingGenerationSignature(pendingGenerationSignature),
    [pendingGenerationSignature]
  );
  const persistedDurationMs = useMemo(() => {
    const parsedDurationMs = toFinite(workspaceTimelineDurationMs);
    return parsedDurationMs === null ? null : Math.max(0, parsedDurationMs);
  }, [workspaceTimelineDurationMs]);
  const {
    selectedLayerIdSet,
    separatingSoundTrackIdSet,
    mergingTrackIdSet,
    tracks,
    maxDeclaredTrackEndMs,
    durationMs,
    resolvedTracks,
    visibleResolvedTracks,
    previewResolvedTracks,
    laneRows,
    displayTrackOrderLayerIds,
    maxLaneOrder,
    timelineLanePresentationRows,
    hasAnyTrack,
    hasPlayableMediaLayers,
    hasPlayableMediaTrack,
    clampedCursorMs,
    playbackStartMs,
    playbackEndMs,
    rulerRangeStartMs,
    rulerRangeEndMs,
    rulerRangeMs,
    timelineDropPlaceholderDurationMs,
    timelineRenderRangeStartMs: baseTimelineRenderRangeStartMs,
    timelineRenderRangeEndMs: baseTimelineRenderRangeEndMs,
  } = useWorkspaceTimelineTrackProjection({
    projectId,
    workspaceId,
    cursorMs,
    persistedDurationMs,
    pendingVideoGenerationTracks,
    mediaDurationVersion,
    probedTrackDurationsRef,
    separatingSoundTrackIds,
    mergingTrackIds,
    activeDragTrackId,
    activeTrackOrderDragId,
    activeTrackDragInitialLaneId,
    splitHoverTrackId,
    splitPreview,
    splitScissorPulseFrame,
    trackDragTimingPreview,
    trackLaneDragPreview,
    timelineFileDropPreview,
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // On veut justement le contenu du ref AU DEMONTAGE, pas celui capture au montage :
      // ce sont les merges encore en vol a cet instant qu'il faut annuler. Copier la valeur
      // dans une variable de l'effet, ce que suggere la regle, annulerait un ensemble vide
      // et laisserait les merges tourner.
      mergeAbortControllersRef.current.forEach((controller) => controller.abort());
      mergeAbortControllersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    resolvedTracksRef.current = resolvedTracks;
  }, [resolvedTracks]);

  useEffect(() => {
    laneRowsRef.current = laneRows;
  }, [laneRows]);

  useEffect(() => {
    selectedLayerIdSetRef.current = selectedLayerIdSet;
  }, [selectedLayerIdSet]);

  useEffect(() => {
    displayTrackOrderLayerIdsRef.current = displayTrackOrderLayerIds;
  }, [displayTrackOrderLayerIds]);

  useEffect(() => {
    maxLaneOrderRef.current = maxLaneOrder;
  }, [maxLaneOrder]);

  useEffect(() => {
    rulerRangeStartMsRef.current = rulerRangeStartMs;
  }, [rulerRangeStartMs]);

  useEffect(() => {
    rulerRangeEndMsRef.current = rulerRangeEndMs;
  }, [rulerRangeEndMs]);

  useEffect(() => {
    rulerRangeMsRef.current = rulerRangeMs;
  }, [rulerRangeMs]);

  const {
    volumeEditorTrackIdRef,
    volumeOverlayTrack,
    timelineVolumeActions,
    openVolumeEditorForTrack,
    closeVolumeEditor,
    startVolumeHold,
  } = useWorkspaceTimelineVolumeController({
    kernel,
    resolvedTracks,
  });

  const showVersionView = useMemo(() => {
    if (hasPlayableMediaLayers) {
      return workspacePreferVersionPanelOverTimeline;
    }
    return workspaceImageTimelineManuallyEnabled ? false : true;
  }, [hasPlayableMediaLayers, workspaceImageTimelineManuallyEnabled, workspacePreferVersionPanelOverTimeline]);
  const onToggleVersionView = useCallback(() => {
    kernel.current?.toggleWorkspaceTimelineView(projectId, workspaceId);
  }, [kernel, projectId, workspaceId]);
  const {
    timelinePlaybackRate,
    isAtTimelineEnd,
    isComposedPreparing,
    primaryControlLabel,
    playbackRateLabel,
    clampedCursorMsRef,
    rulerRef,
    handlePrimaryAction,
    handleStepBackward,
    handleStepForward,
    handleToggleLoop,
    handleToggleMuted,
    handleSelectPlaybackRate,
    handleToggleVersionView,
    handleRulerMouseDown,
    seekPlaybackCursor,
    pausePlaybackForEdit,
  } = useWorkspaceTimelinePlaybackController({
    kernel,
    workspaceId,
    isActive: onScreen,
    showVersionView,
    clampedCursorMs,
    durationMs,
    playbackStartMs,
    playbackEndMs,
    hasAnyTrack,
    hasPlayableMediaTrack,
    resolvedTracks,
    visibleResolvedTracks,
    previewResolvedTracks,
    workspaceFrameW,
    workspaceFrameH,
    workspaceTimelineFps,
    rulerRangeStartMs,
    rulerRangeEndMs,
    rulerRangeMs,
    onCursorChange,
    onToggleVersionView,
    composedPlaybackVideoRef,
    playbackMediaClockHintRef,
    onComposedPlaybackStateChange,
  });

  const playbackRateMenuActions = useMemo<QuickMenuAction[]>(() => (
    TIMELINE_PLAYBACK_RATE_OPTIONS.map((rate) => ({
      label: formatTimelinePlaybackRateOption(rate),
      selected: Math.abs(timelinePlaybackRate - rate) < TIMELINE_PLAYBACK_RATE_EPSILON,
      onClick: () => {
        handleSelectPlaybackRate(rate);
      },
    }))
  ), [handleSelectPlaybackRate, timelinePlaybackRate]);

  const handleOpenPlaybackRateMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!hasAnyTrack) return;
    const rect = event.currentTarget.getBoundingClientRect();
    kernel.current?.setMenuData({
      position: {
        x: rect.left + rect.width / 2,
        y: rect.bottom + 6,
      },
      actions: playbackRateMenuActions,
    });
  }, [hasAnyTrack, kernel, playbackRateMenuActions]);

  const associatedVideoLayerIdsFromVisibleAudio = useMemo(() => {
    const associatedLayerIds = new Set<string>();
    visibleResolvedTracks.forEach((track) => {
      if (track.layerType !== 'audio') return;
      const parentLayerId =
        typeof track.separatedFromLayerId === 'string'
          ? track.separatedFromLayerId.trim()
          : '';
      if (!parentLayerId) return;
      associatedLayerIds.add(parentLayerId);
    });
    return associatedLayerIds;
  }, [visibleResolvedTracks]);
  const isVideoTimelineAudioMuted = useCallback((track: WorkspaceTimelineTrack): boolean => {
    if (track.layerType !== 'video') return false;
    if (track.mutedInTimeline) return true;
    return associatedVideoLayerIdsFromVisibleAudio.has(track.layerId);
  }, [associatedVideoLayerIdsFromVisibleAudio]);
  const selectedVideoTracksForMerge = useMemo(() => (
    resolvedTracks.filter((track) => (
      track.layerType === 'video' && track.isLayerVisible && selectedLayerIdSet.has(track.layerId)
    ))
  ), [resolvedTracks, selectedLayerIdSet]);

  useEffect(() => {
    if (activeTool !== 'scissors' || !splitHoverTrackId) {
      setSplitScissorPulseFrame(false);
      return;
    }
    const intervalId = window.setInterval(() => {
      setSplitScissorPulseFrame((current) => !current);
    }, 190);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeTool, splitHoverTrackId]);

  useEffect(() => {
    if (activeTool === 'scissors') return;
    setSplitHoverTrackId(null);
    setSplitPreview(null);
  }, [activeTool]);

  useEffect(() => {
    if (!isActive || showVersionView) {
      setTimelineFileDropPreview(null);
    }
  }, [isActive, showVersionView]);

  useEffect(() => {
    if (!kernel.current) return;
    if (tracks.length === 0) return;
    if (!Number.isFinite(maxDeclaredTrackEndMs) || maxDeclaredTrackEndMs <= 0) return;
    const currentWorkspace = kernel.current?.getWorkspace(workspaceId);
    const currentDurationMs = toFinite(currentWorkspace?.timelineDurationMs);
    if (currentDurationMs !== null && Math.abs(currentDurationMs - maxDeclaredTrackEndMs) <= 1) {
      lastAutoDurationWriteRef.current = maxDeclaredTrackEndMs;
      return;
    }
    // Always heal divergence, even if we already attempted this value before.
    // Previous guards could lock the UI into an overgrown duration after transient updates.
    lastAutoDurationWriteRef.current = maxDeclaredTrackEndMs;
    kernel.current?.setWorkspace(workspaceId, (currentWorkspaceSnapshot) => ({
      ...currentWorkspaceSnapshot,
      timelineDurationMs: maxDeclaredTrackEndMs,
    }), projectId);
  }, [kernel, maxDeclaredTrackEndMs, projectId, tracks.length, workspaceId]);

  useEffect(() => {
    const videoRefs = kernel.current?.videoRefs.current;
    if (!videoRefs) return;
    const cleanups: Array<() => void> = [];
    tracks.forEach((track) => {
      if (track.layerType !== 'video') return;
      const videoElement = videoRefs[track.layerId];
      if (!videoElement) return;
      const handleMetadata = () => {
        setMediaDurationVersion((current) => current + 1);
      };
      videoElement.addEventListener('loadedmetadata', handleMetadata);
      videoElement.addEventListener('durationchange', handleMetadata);
      cleanups.push(() => {
        videoElement.removeEventListener('loadedmetadata', handleMetadata);
        videoElement.removeEventListener('durationchange', handleMetadata);
      });
    });
    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [kernel, tracks]);

  // Re-run the duration probe when a video layer's hydrated blob becomes available, so the
  // probe can read the (local) blob instead of ever touching the raw asset URL.
  const videoBlobReadySignature = useKernelStore((state) => {
    const ws = state.inventory.StudioWorkspace.registry[workspaceId];
    const ids = Array.isArray(ws?.layerIds) ? ws.layerIds : [];
    let signature = '';
    for (const id of ids) {
      const layer = state.inventory.StudioLayer.registry[id];
      if (layer?.type === 'video' && layer.videoBlob) signature += `${id};`;
    }
    return signature;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const videoTracks = tracks
      .filter((track) => track.layerType === 'video' || track.layerType === 'audio')
      .map((track) => ({
        layerId: track.layerId,
        layerType: track.layerType,
        src: getTrackSource(track),
      }))
      .filter((track) => track.src.length > 0);
    if (videoTracks.length === 0) return;
    const layerRegistry = kernel.current?.getLayerRegistry?.() ?? {};

    const knownLayerIds = new Set(videoTracks.map((track) => track.layerId));
    probedTrackDurationsRef.current.forEach((_, layerId) => {
      if (!knownLayerIds.has(layerId)) {
        probedTrackDurationsRef.current.delete(layerId);
      }
    });

    const cleanups: Array<() => void> = [];
    videoTracks.forEach((track) => {
      const cachedDuration = probedTrackDurationsRef.current.get(track.layerId);
      if (cachedDuration && cachedDuration.src === track.src && cachedDuration.durationMs > 0) {
        return;
      }
      // Video: probe the hydrated local blob, never the raw URL, streaming metadata reads
      // fail with NS_ERROR (moov at end) and compete with the hydrator's download. Skip until
      // the blob exists (this effect re-runs when it arrives). Audio keeps its source URL
      // (audio metadata sits at the head and reads fine).
      let videoBlob: Blob | null = null;
      if (track.layerType === 'video') {
        videoBlob = (layerRegistry[track.layerId]?.videoBlob as Blob | null | undefined) ?? null;
        if (!videoBlob) return;
      }
      const pendingKey = `${track.layerId}::${track.src}`;
      if (pendingDurationProbeKeysRef.current.has(pendingKey)) {
        return;
      }
      pendingDurationProbeKeysRef.current.add(pendingKey);

      const probeObjectUrl = videoBlob ? URL.createObjectURL(videoBlob) : null;
      const probeSrc = probeObjectUrl ?? track.src;
      const probeElement = document.createElement('video');
      let finalized = false;
      const finalize = (durationMs?: number) => {
        if (finalized) return;
        finalized = true;
        pendingDurationProbeKeysRef.current.delete(pendingKey);
        if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) {
          return;
        }
        const roundedDurationMs = Math.max(1, Math.round(durationMs));
        const existingDuration = probedTrackDurationsRef.current.get(track.layerId);
        if (
          existingDuration &&
          existingDuration.src === track.src &&
          Math.abs(existingDuration.durationMs - roundedDurationMs) < 1
        ) {
          return;
        }
        probedTrackDurationsRef.current.set(track.layerId, {
          src: track.src,
          durationMs: roundedDurationMs,
        });
        setMediaDurationVersion((current) => current + 1);
      };

      const handleMetadata = () => {
        const mediaDurationSec = probeElement.duration;
        if (!Number.isFinite(mediaDurationSec) || mediaDurationSec <= 0) return;
        finalize(mediaDurationSec * 1000);
      };
      const handleError = () => {
        finalize();
      };

      probeElement.preload = 'metadata';
      probeElement.crossOrigin = 'anonymous';
      probeElement.muted = true;
      probeElement.playsInline = true;
      probeElement.addEventListener('loadedmetadata', handleMetadata);
      probeElement.addEventListener('durationchange', handleMetadata);
      probeElement.addEventListener('error', handleError);
      probeElement.src = probeSrc;
      try {
        probeElement.load();
      } catch {
        finalize();
      }

      cleanups.push(() => {
        probeElement.removeEventListener('loadedmetadata', handleMetadata);
        probeElement.removeEventListener('durationchange', handleMetadata);
        probeElement.removeEventListener('error', handleError);
        pendingDurationProbeKeysRef.current.delete(pendingKey);
        try {
          probeElement.removeAttribute('src');
          probeElement.load();
        } catch {
          // no-op cleanup best effort
        }
        if (probeObjectUrl) URL.revokeObjectURL(probeObjectUrl);
      });
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [tracks, videoBlobReadySignature, kernel]);

  useEffect(() => {
    if (!onScreen || showVersionView) return;

    const isTypingTarget = (target: EventTarget | null): boolean => {
      const element = target as HTMLElement | null;
      if (!element) return false;
      if (element.isContentEditable) return true;
      const tagName = element.tagName?.toLowerCase();
      return tagName === 'input' || tagName === 'textarea' || tagName === 'select' || tagName === 'button';
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const isDeleteKey = event.key === 'Delete' || event.key === 'Backspace';
      if (!isDeleteKey) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.repeat) return;
      if (isTypingTarget(event.target)) return;
      if (selectedLayerIdSet.size === 0) return;

      if (!kernel.current) return;

      event.preventDefault();
      event.stopPropagation();
      const layerIdsToRemove = Array.from(selectedLayerIdSet);
      for (const layerId of layerIdsToRemove) {
        try {
          kernel.current?.removeLayer(layerId);
        } catch (error) {
          console.error('[WorkspaceTimelinePanel] removeLayer failed', { layerId, error });
        }
        if (volumeEditorTrackIdRef.current === layerId) {
          closeVolumeEditor();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onScreen, showVersionView, selectedLayerIdSet, kernel, volumeEditorTrackIdRef, closeVolumeEditor]);

  const splitTrackAtMs = useCallback((track: (typeof resolvedTracks)[number], splitCursorMsRaw: number) => {
    if (!kernel.current) return;

    const splitCursorMs = Math.round(clamp(splitCursorMsRaw, track.startMs, track.endMs));
    const leftDurationMs = splitCursorMs - track.startMs;
    const rightDurationMs = track.endMs - splitCursorMs;
    if (leftDurationMs < MIN_TRACK_SPAN_MS || rightDurationMs < MIN_TRACK_SPAN_MS) {
      toast.warning(`Split point is too close to an edge. Keep at least ${MIN_TRACK_SPAN_MS}ms on each side.`);
      return;
    }

    const workspaceSnapshot = kernel.current?.getWorkspace(workspaceId);
    if (!workspaceSnapshot) {
      toast.warning('Workspace not found.');
      return;
    }
    const preservedTimelineDurationMs = Math.max(
      1,
      Math.round(toFinite(workspaceSnapshot.timelineDurationMs) ?? durationMs)
    );

    const workspaceLayerIdsBefore = Array.isArray(workspaceSnapshot.layerIds)
      ? workspaceSnapshot.layerIds.filter((layerId): layerId is string => typeof layerId === 'string')
      : [];
    const originalLayerId = track.layerId;
    const originalLayerIndex = workspaceLayerIdsBefore.indexOf(originalLayerId);
    if (originalLayerIndex < 0) {
      toast.warning('Track layer is no longer available.');
      return;
    }

    const originalLayer = kernel.current.getLayerRegistry()[originalLayerId];
    if (!originalLayer) {
      toast.warning('Track layer is no longer available.');
      return;
    }

    const findNewLayerId = (beforeIds: Set<string>, afterIds: string[]): string | null => {
      for (const layerId of afterIds) {
        if (!beforeIds.has(layerId)) return layerId;
      }
      return null;
    };

    const firstBeforeIds = new Set(workspaceLayerIdsBefore);
    kernel.current.duplicateLayer(originalLayerId);
    const workspaceAfterFirstDuplicate = kernel.current?.getWorkspace(workspaceId);
    const layerIdsAfterFirstDuplicate = Array.isArray(workspaceAfterFirstDuplicate?.layerIds)
      ? workspaceAfterFirstDuplicate.layerIds
      : [];
    const leftLayerId = findNewLayerId(firstBeforeIds, layerIdsAfterFirstDuplicate);
    if (!leftLayerId) {
      toast.warning('Failed to split track: could not create the first segment.');
      return;
    }

    const secondBeforeIds = new Set(layerIdsAfterFirstDuplicate);
    kernel.current.duplicateLayer(originalLayerId);
    const workspaceAfterSecondDuplicate = kernel.current?.getWorkspace(workspaceId);
    const layerIdsAfterSecondDuplicate = Array.isArray(workspaceAfterSecondDuplicate?.layerIds)
      ? workspaceAfterSecondDuplicate.layerIds
      : [];
    const rightLayerId = findNewLayerId(secondBeforeIds, layerIdsAfterSecondDuplicate);
    if (!rightLayerId) {
      toast.warning('Failed to split track: could not create the second segment.');
      return;
    }

    const baseName = originalLayer.name || 'Video';
    const sourceInForTrack = track.layerType === 'video' ? Math.round(track.sourceInMs) : 0;
    const rightSourceInMs = track.layerType === 'video'
      ? Math.round(track.sourceInMs + leftDurationMs)
      : 0;
    const originalLayerSnapshot = cloneTimelineValue(kernel.current.getLayerRegistry()[originalLayerId]);
    const leftLayerSnapshot = cloneTimelineValue(kernel.current.getLayerRegistry()[leftLayerId]);
    const rightLayerSnapshot = cloneTimelineValue(kernel.current.getLayerRegistry()[rightLayerId]);
    const timelineDurationMsBefore = Math.max(
      1,
      Math.round(toFinite(workspaceSnapshot.timelineDurationMs) ?? durationMs)
    );
    const timelineCursorMsBefore = clamp(
      Math.max(0, Math.round(toFinite(workspaceSnapshot.timelineCursorMs) ?? 0)),
      0,
      Math.max(0, timelineDurationMsBefore - 1)
    );

    kernel.current.executeAction({
      description: `Split track "${baseName}"`,
      redo: () => {
        if (!kernel.current) return;
        kernel.current.setLayer(originalLayerId, (existingLayer) => ({
          ...existingLayer,
          visible: false,
          params: mergeTimelineAudioParams(existingLayer.params, { [TIMELINE_REPLACED_BY_EDIT_PARAM_KEY]: true }),
          pulseToken: Date.now(),
        }));
        kernel.current.setLayer(leftLayerId, (existingLayer) => ({
          ...existingLayer,
          visible: true,
          params: mergeTimelineAudioParams(existingLayer.params, { [TIMELINE_REPLACED_BY_EDIT_PARAM_KEY]: null }),
          name: `${baseName} (A)`,
          timelineStartMs: Math.round(track.startMs),
          timelineEndMs: splitCursorMs,
          timelineSourceInMs: sourceInForTrack,
          pulseToken: Date.now() + 1,
        }));
        kernel.current.setLayer(rightLayerId, (existingLayer) => ({
          ...existingLayer,
          visible: true,
          params: mergeTimelineAudioParams(existingLayer.params, { [TIMELINE_REPLACED_BY_EDIT_PARAM_KEY]: null }),
          name: `${baseName} (B)`,
          timelineStartMs: splitCursorMs,
          timelineEndMs: Math.round(track.endMs),
          timelineSourceInMs: rightSourceInMs,
          pulseToken: Date.now() + 2,
        }));

        kernel.current.setWorkspace(workspaceId, (existingWorkspace) => {
          const currentLayerIds = Array.isArray(existingWorkspace.layerIds)
            ? existingWorkspace.layerIds.filter((layerId): layerId is string => typeof layerId === 'string')
            : [];
          const withoutSplitGroup = currentLayerIds.filter(
            (layerId) => layerId !== originalLayerId && layerId !== leftLayerId && layerId !== rightLayerId
          );
          const insertionIndex = clamp(originalLayerIndex, 0, withoutSplitGroup.length);
          const nextLayerIds = [
            ...withoutSplitGroup.slice(0, insertionIndex),
            leftLayerId,
            rightLayerId,
            ...withoutSplitGroup.slice(insertionIndex),
          ];
          nextLayerIds.forEach((layerId, index) => {
            kernel.current?.setLayer(layerId, (existingLayer) => ({
              ...existingLayer,
              renderOrder: index,
            }));
          });
          return {
            ...existingWorkspace,
            layerIds: nextLayerIds,
            timelineDurationMs: preservedTimelineDurationMs,
            timelineCursorMs: clamp(
              Math.max(0, Number(existingWorkspace.timelineCursorMs ?? 0)),
              0,
              Math.max(0, preservedTimelineDurationMs - 1)
            ),
          };
        }, projectId);
      },
      undo: () => {
        if (!kernel.current) return;
        const nextOriginalLayer = cloneTimelineValue(originalLayerSnapshot);
        const nextLeftLayer = {
          ...cloneTimelineValue(leftLayerSnapshot),
          visible: false,
          pulseToken: Date.now() + 3,
        };
        const nextRightLayer = {
          ...cloneTimelineValue(rightLayerSnapshot),
          visible: false,
          pulseToken: Date.now() + 4,
        };
        kernel.current.setLayer(originalLayerId, (existingLayer) => (
          nextOriginalLayer ? { ...nextOriginalLayer } : existingLayer
        ));
        kernel.current.setLayer(leftLayerId, (existingLayer) => ({
          ...existingLayer,
          ...(nextLeftLayer ?? {}),
          visible: false,
          params: mergeTimelineAudioParams(nextLeftLayer?.params ?? existingLayer.params, { [TIMELINE_REPLACED_BY_EDIT_PARAM_KEY]: true }),
          pulseToken: Date.now() + 3,
        }));
        kernel.current.setLayer(rightLayerId, (existingLayer) => ({
          ...existingLayer,
          ...(nextRightLayer ?? {}),
          visible: false,
          params: mergeTimelineAudioParams(nextRightLayer?.params ?? existingLayer.params, { [TIMELINE_REPLACED_BY_EDIT_PARAM_KEY]: true }),
          pulseToken: Date.now() + 4,
        }));
        kernel.current.setWorkspace(workspaceId, (existingWorkspace) => {
          const nextLayerIds = [...workspaceLayerIdsBefore];
          nextLayerIds.forEach((layerId, index) => {
            kernel.current?.setLayer(layerId, (existingLayer) => ({
              ...existingLayer,
              renderOrder: index,
            }));
          });
          return {
            ...existingWorkspace,
            layerIds: nextLayerIds,
            timelineDurationMs: timelineDurationMsBefore,
            timelineCursorMs: timelineCursorMsBefore,
          };
        }, projectId);
      },
    });
  }, [durationMs, kernel, projectId, workspaceId]);

  useEffect(() => {
    splitTrackAtMsRef.current = splitTrackAtMs;
  }, [splitTrackAtMs]);

  const separateTrackAudio = useCallback(async (track: (typeof resolvedTracks)[number]) => {
    if (!kernel.current) return;
    if (track.layerType !== 'video') {
      toast.warning('Audio separation only supports video tracks.');
      return;
    }
    if (track.mutedInTimeline) {
      toast.warning('This video track is already silent in timeline playback.');
      return;
    }

    const workspaceSnapshot = kernel.current?.getWorkspace(workspaceId);
    if (!workspaceSnapshot) {
      toast.warning('Workspace not found.');
      return;
    }
    const workspaceLayerIdsBefore = Array.isArray(workspaceSnapshot.layerIds)
      ? workspaceSnapshot.layerIds.filter((layerId): layerId is string => typeof layerId === 'string')
      : [];
    const originalLayerId = track.layerId;
    const originalLayerIndex = workspaceLayerIdsBefore.indexOf(originalLayerId);
    if (originalLayerIndex < 0) {
      toast.warning('Track layer is no longer available.');
      return;
    }

    const originalLayer = kernel.current.getLayerRegistry()[originalLayerId];
    if (!originalLayer || originalLayer.type !== 'video') {
      toast.warning('Audio separation only supports video tracks.');
      return;
    }

    const source = typeof originalLayer.videoSrc === 'string' ? originalLayer.videoSrc.trim() : '';
    if (source.length === 0) {
      toast.warning('This video track has no source to separate.');
      return;
    }

    if (separatingSoundTrackLockRef.current.has(track.layerId)) {
      toast.warning('Audio split is already in progress for this track.');
      return;
    }
    separatingSoundTrackLockRef.current.add(track.layerId);
    setSeparatingSoundTrackIds((previous) => (
      previous.includes(track.layerId) ? previous : [...previous, track.layerId]
    ));

    let extractedAudioSrc = '';
    try {
      try {
        const extractionAbortController = new AbortController();
        const extractionTimeoutId = window.setTimeout(() => {
          extractionAbortController.abort();
        }, AUDIO_SPLIT_REQUEST_TIMEOUT_MS);
        try {
          extractedAudioSrc = await extractAudioFromVideo({
            sourceSrc: source,
            timeoutMs: AUDIO_SPLIT_REQUEST_TIMEOUT_MS,
            abortSignal: extractionAbortController.signal,
          });
        } finally {
          window.clearTimeout(extractionTimeoutId);
        }
      } catch (error: any) {
        const isAbortError = error?.name === 'AbortError';
        if (isAbortError) {
          toast.warning('Audio split timed out. Worker is likely busy or offline. Please try again.');
        } else {
          toast.warning(error?.message || 'Failed to extract audio from this video.');
        }
        return;
      }

      const duplicateBeforeIds = new Set(workspaceLayerIdsBefore);
      kernel.current.duplicateLayer(originalLayerId);
      const workspaceAfterDuplicate = kernel.current?.getWorkspace(workspaceId);
      const layerIdsAfterDuplicate = Array.isArray(workspaceAfterDuplicate?.layerIds)
        ? workspaceAfterDuplicate.layerIds
        : [];
      const audioLayerId = layerIdsAfterDuplicate.find((layerId) => !duplicateBeforeIds.has(layerId)) ?? null;
      if (!audioLayerId) {
        toast.warning('Failed to create audio track layer.');
        return;
      }

      const baseName = originalLayer.name || 'Video';
      const shouldHideVideoAfterSeparation = originalLayer.source === 'mirelo-sfx-1.6';
      const originalParams = originalLayer.params;
      const originalLayerSnapshot = cloneTimelineValue(kernel.current.getLayerRegistry()[originalLayerId]);
      const audioLayerSnapshot = cloneTimelineValue(kernel.current.getLayerRegistry()[audioLayerId]);

      kernel.current.executeAction({
        description: `Separate audio from "${baseName}"`,
        redo: () => {
          if (!kernel.current) return;
          kernel.current.setLayer(originalLayerId, (existingLayer) => {
            return {
              ...existingLayer,
              ...(shouldHideVideoAfterSeparation ? { visible: false } : {}),
              params: mergeTimelineAudioParams(existingLayer.params ?? originalParams, {
                [TIMELINE_MUTE_AUDIO_PARAM_KEY]: true,
              }),
            };
          });
          kernel.current.setLayer(audioLayerId, (existingLayer) => {
            return {
              ...existingLayer,
              type: 'audio',
              visible: true,
              name: `${baseName} (Audio)`,
              videoSrc: extractedAudioSrc,
              imageData: null,
              imageSrc: undefined,
              videoFramesUrls: [],
              timelineStartMs: Math.round(track.startMs),
              timelineEndMs: Math.round(track.endMs),
              timelineSourceInMs: Math.round(track.sourceInMs),
              params: mergeTimelineAudioParams(existingLayer.params ?? originalParams, {
                [TIMELINE_AUDIO_ONLY_PARAM_KEY]: true,
                [TIMELINE_MUTE_AUDIO_PARAM_KEY]: false,
                [TIMELINE_AUDIO_GAIN_PARAM_KEY]: 1,
                [TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY]: originalLayerId,
              }),
            };
          });
          const originalVideoElement = kernel.current.videoRefs.current[originalLayerId];
          if (originalVideoElement) {
            originalVideoElement.muted = true;
            originalVideoElement.volume = 0;
          }
          kernel.current.setWorkspace(workspaceId, (existingWorkspace) => {
            const currentLayerIds = Array.isArray(existingWorkspace.layerIds)
              ? existingWorkspace.layerIds.filter((layerId): layerId is string => typeof layerId === 'string')
              : [];
            const withoutAudioLayer = currentLayerIds.filter((layerId) => layerId !== audioLayerId);
            const insertionIndex = clamp(originalLayerIndex + 1, 0, withoutAudioLayer.length);
            const nextLayerIds = [
              ...withoutAudioLayer.slice(0, insertionIndex),
              audioLayerId,
              ...withoutAudioLayer.slice(insertionIndex),
            ];
            nextLayerIds.forEach((layerId, index) => {
              kernel.current?.setLayer(layerId, (existingLayer) => ({
                ...existingLayer,
                renderOrder: index,
              }));
            });
            return {
              ...existingWorkspace,
              layerIds: nextLayerIds,
            };
          }, projectId);
        },
        undo: () => {
          if (!kernel.current) return;
          const nextOriginalLayer = cloneTimelineValue(originalLayerSnapshot);
          const nextAudioLayer = {
            ...cloneTimelineValue(audioLayerSnapshot),
            visible: false,
            pulseToken: Date.now() + 5,
          };
          kernel.current.setLayer(originalLayerId, (existingLayer) => (
            nextOriginalLayer ? { ...nextOriginalLayer } : existingLayer
          ));
          kernel.current.setLayer(audioLayerId, (existingLayer) => ({
            ...existingLayer,
            ...(nextAudioLayer ?? {}),
            visible: false,
            pulseToken: Date.now() + 5,
          }));
          kernel.current.setWorkspace(workspaceId, (existingWorkspace) => {
            const nextLayerIds = [...workspaceLayerIdsBefore];
            nextLayerIds.forEach((layerId, index) => {
              kernel.current?.setLayer(layerId, (existingLayer) => ({
                ...existingLayer,
                renderOrder: index,
              }));
            });
            return {
              ...existingWorkspace,
              layerIds: nextLayerIds,
            };
          }, projectId);
        },
      });
    } finally {
      separatingSoundTrackLockRef.current.delete(track.layerId);
      setSeparatingSoundTrackIds((previous) => previous.filter((layerId) => layerId !== track.layerId));
    }
  }, [kernel, projectId, workspaceId]);

  useEffect(() => {
    separateTrackAudioRef.current = separateTrackAudio;
  }, [separateTrackAudio]);

  const selectTrackFromPointer = useCallback((
    track: { layerId: string },
    event: { button: number; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean },
    forceSingleSelection = false,
  ) => {
    if (event.button !== 0) return;
    const isMultiSelect = !forceSingleSelection && (event.ctrlKey || event.metaKey || event.shiftKey);
    kernel.current?.selectLayer(projectId, track.layerId, isMultiSelect);
  }, [kernel, projectId]);

  const {
    timelineInsertEndLaneRef,
    handleTimelineDragOver,
    handleTimelineDragLeave,
    handleTimelineDrop,
    registerTrackLaneRef,
    startTrackDrag,
    startTrackOrderDrag,
    resolveSplitMsFromLanePointer,
    resolveTrackFromLanePointer,
  } = useWorkspaceTimelineInteractionController({
    kernel,
    projectId,
    workspaceId,
    showVersionView,
    activeTool,
    resolvedTracks,
    resolvedTracksRef,
    laneRowsRef,
    displayTrackOrderLayerIdsRef,
    maxLaneOrderRef,
    rulerRangeStartMsRef,
    rulerRangeEndMsRef,
    rulerRangeMsRef,
    clampedCursorMsRef,
    rulerRef,
    pausePlaybackForEdit,
    selectTrackFromPointer,
    setActiveDragTrackId,
    setActiveTrackDragInitialLaneId,
    setActiveTrackOrderDragId,
    setDragSnapGuideMs,
    setTrackDragTimingPreview,
    setTrackLaneDragPreview,
    setTimelineFileDropPreview,
    setShowEndInsertLaneWhileDragging,
  });

  const handleTrackTimelineToolMouseDown = useCallback((
    event: React.MouseEvent<HTMLElement>,
    track: (typeof resolvedTracks)[number]
  ) => {
    const isSplitToolActive = activeTool === 'scissors';
    const isSoundSeparationToolActive = activeTool === 'sound' && activeSoundTool === 'separation';
    const isSoundVolumeToolActive = activeTool === 'sound' && activeSoundTool === 'volume';
    if (!isSplitToolActive && !isSoundSeparationToolActive && !isSoundVolumeToolActive) return;

    if (isSoundVolumeToolActive) {
      if (event.button !== 0 && event.button !== 2) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.button === 0) {
        selectTrackFromPointer(track, event);
      }
      startVolumeHold(track, event.button === 2 ? 'up' : 'down');
      return;
    }

    if (event.button !== 0) return;
    selectTrackFromPointer(track, event);
    event.preventDefault();
    event.stopPropagation();

    if (isSoundSeparationToolActive) {
      void separateTrackAudioRef.current(track);
      return;
    }

    const laneElement = event.currentTarget.closest('[data-track-lane="true"]') as HTMLElement | null;
    if (!laneElement) return;
    const splitMs = resolveSplitMsFromLanePointer(track, laneElement, event.clientX);
    if (splitMs === null) return;

    seekPlaybackCursor(splitMs);
    setSplitPreview({ trackId: track.layerId, splitMs });
    splitTrackAtMsRef.current(track, splitMs);
  }, [
    activeSoundTool,
    activeTool,
    resolveSplitMsFromLanePointer,
    seekPlaybackCursor,
    selectTrackFromPointer,
    startVolumeHold,
  ]);

  const mergeSelectedVideoTracks = useCallback(async () => {
    if (!kernel.current) return;
    const orderedSelectedTracks = selectedVideoTracksForMerge
      .filter((track) => track.layerType === 'video')
      .sort((left, right) => {
        if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
        return left.layerId.localeCompare(right.layerId);
      });
    if (orderedSelectedTracks.length < 2) {
      toast.warning('Select at least two video tracks to merge.');
      return;
    }

    const normalizedTracks = orderedSelectedTracks
      .map((track) => {
        const sourceSrc = typeof track.videoSrc === 'string' ? track.videoSrc.trim() : '';
        if (!sourceSrc) return null;
        return {
          layerId: track.layerId,
          layerType: 'video' as const,
          sourceSrc,
          videoSrc: sourceSrc,
          imageSrc: null,
          audioEnabled: !isVideoTimelineAudioMuted(track),
          audioGain: getNormalizedAudioGain(track.audioGain),
          renderOrder: track.renderOrder,
          startMs: Math.max(0, Math.round(track.startMs)),
          endMs: Math.max(Math.round(track.startMs + 1), Math.round(track.endMs)),
          sourceInMs: Math.max(0, Math.round(track.sourceInMs)),
          rect: {
            x: Number.isFinite(track.rect?.x) ? Number(track.rect.x) : 0,
            y: Number.isFinite(track.rect?.y) ? Number(track.rect.y) : 0,
            w: Number.isFinite(track.rect?.w) ? Number(track.rect.w) : 0,
            h: Number.isFinite(track.rect?.h) ? Number(track.rect.h) : 0,
          },
          scale: typeof track.scale === 'number' && Number.isFinite(track.scale) ? track.scale : 1,
        };
      })
      .filter((track): track is NonNullable<typeof track> => Boolean(track));

    if (normalizedTracks.length < 2) {
      toast.warning('Could not resolve enough valid video sources to merge.');
      return;
    }

    const mergeRangeStartMs = normalizedTracks.reduce((min, track) => Math.min(min, track.startMs), Number.POSITIVE_INFINITY);
    const mergeRangeEndMs = normalizedTracks.reduce((max, track) => Math.max(max, track.endMs), 0);
    if (!Number.isFinite(mergeRangeStartMs) || mergeRangeEndMs <= mergeRangeStartMs) {
      toast.warning('Invalid selected video range for merge.');
      return;
    }

    const outputWidth = normalizeVideoDimension(toFinite(workspaceFrameW) ?? 1280);
    const outputHeight = normalizeVideoDimension(toFinite(workspaceFrameH) ?? 720);
    const outputFps = Math.max(8, Math.min(30, Math.round(toFinite(workspaceTimelineFps) ?? 24)));

    type MergeComposeStatus = {
      status?: 'processing' | 'ready' | 'error' | 'missing';
      phase?: 'queued' | 'pulling' | 'building' | 'finalizing' | 'ready' | 'error' | 'missing';
      percent?: number;
      message?: string;
      error?: string;
    };
    type MergeComposeEnvelope = { cacheKey?: string; status?: MergeComposeStatus; realtime?: { enabled?: boolean } };

    const requestBody = {
      workspaceId,
      output: {
        width: outputWidth,
        height: outputHeight,
        fps: outputFps,
        requireMp4: true,
        includeAudio: true,
        profile: 'export' as const,
      },
      range: {
        startMs: Math.max(0, Math.round(mergeRangeStartMs)),
        endMs: Math.max(Math.round(mergeRangeStartMs + 1), Math.round(mergeRangeEndMs)),
      },
      tracks: normalizedTracks,
    };

    const mergingLayerIds = normalizedTracks.map((track) => track.layerId);
    const mergingLayerIdSet = new Set(mergingLayerIds);
    if (mergingLayerIds.some((layerId) => mergingTrackLockRef.current.has(layerId))) {
      toast.warning('Merge is already in progress for one of the selected tracks.');
      return;
    }
    mergingLayerIds.forEach((layerId) => mergingTrackLockRef.current.add(layerId));
    setMergingTrackIds((previous) => {
      const next = new Set(previous);
      mergingLayerIds.forEach((layerId) => next.add(layerId));
      return Array.from(next);
    });

    let cacheKey = '';
    const mergeAbortController = new AbortController();
    mergeAbortControllersRef.current.add(mergeAbortController);
    try {
      toast.info('Merging selected video tracks...');
      const prepareResponse = await fetch('/api/export/timeline-video?mode=prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: mergeAbortController.signal,
      });
      if (!prepareResponse.ok) {
        const errorText = await prepareResponse.text().catch(() => '');
        throw new Error(errorText || `Merge prepare failed (${prepareResponse.status}).`);
      }
      const preparePayload = (await prepareResponse.json()) as MergeComposeEnvelope;
      cacheKey = typeof preparePayload.cacheKey === 'string' ? preparePayload.cacheKey.trim() : '';
      if (!cacheKey) {
        throw new Error('Merge composition did not return a cache key.');
      }
      const statusPayload = await waitForTimelineExportReadyViaSocket({
        kernel,
        cacheKey,
        initialStatus: preparePayload.status as TimelineCompositionStatus | undefined,
        timeoutMs: 6 * 60 * 1000,
        abortSignal: mergeAbortController.signal,
        socketEnabled: preparePayload.realtime?.enabled !== false,
      });
      if (statusPayload.status !== 'ready') {
        throw new Error('Merge composition did not reach ready state.');
      }

      const mergedFileResponse = await fetch(
        `/api/export/timeline-video?mode=file&cacheKey=${encodeURIComponent(cacheKey)}&v=${Date.now()}`,
        { method: 'GET', signal: mergeAbortController.signal }
      );
      if (!mergedFileResponse.ok) {
        const errorText = await mergedFileResponse.text().catch(() => '');
        throw new Error(errorText || `Merge file fetch failed (${mergedFileResponse.status}).`);
      }
      const mergedBlob = await mergedFileResponse.blob();
      if (!mergedBlob || mergedBlob.size <= 0) {
        throw new Error('Merged video file is empty.');
      }

      const uploadedVideoSrc = await uploadFileToCloud(
        mergedBlob,
        `timeline-merged-${Date.now()}.mp4`,
        'video/mp4'
      );
      if (!uploadedVideoSrc || uploadedVideoSrc.trim().length === 0) {
        throw new Error('Failed to upload merged video.');
      }

      const workspaceSnapshot = kernel.current?.getWorkspace(workspaceId);
      if (!workspaceSnapshot) {
        throw new Error('Workspace not found.');
      }
      const workspaceLayerIdsBefore = Array.isArray(workspaceSnapshot.layerIds)
        ? workspaceSnapshot.layerIds.filter((layerId): layerId is string => typeof layerId === 'string')
        : [];
      const layerRegistry = kernel.current.getLayerRegistry();
      const selectedLayerIds = normalizedTracks.map((track) => track.layerId);
      const selectedVisibilityBefore: Record<string, boolean> = selectedLayerIds.reduce((acc, layerId) => {
        const layer = layerRegistry[layerId];
        if (layer) acc[layerId] = layer.visible !== false;
        return acc;
      }, {} as Record<string, boolean>);

      const baseTrack = orderedSelectedTracks[orderedSelectedTracks.length - 1];
      const baseLayerId = baseTrack?.layerId;
      if (!baseLayerId) {
        throw new Error('Could not resolve a base track for merge.');
      }
      const baseName = baseTrack.name || 'Merged Video';
      const beforeIds = new Set(workspaceLayerIdsBefore);
      kernel.current.duplicateLayer(baseLayerId);
      const workspaceAfterDuplicate = kernel.current?.getWorkspace(workspaceId);
      const layerIdsAfterDuplicate = Array.isArray(workspaceAfterDuplicate?.layerIds)
        ? workspaceAfterDuplicate.layerIds
        : [];
      const mergedLayerId = layerIdsAfterDuplicate.find((layerId) => !beforeIds.has(layerId)) ?? null;
      if (!mergedLayerId) {
        throw new Error('Failed to create the merged track layer.');
      }
      const mergedLayerSnapshot = cloneTimelineValue(kernel.current.getLayerRegistry()[mergedLayerId]);
      const insertionIndex = workspaceLayerIdsBefore.reduce((max, layerId, index) => (
        selectedLayerIds.includes(layerId) ? Math.max(max, index) : max
      ), -1) + 1;

      kernel.current.executeAction({
        description: `Merge ${selectedLayerIds.length} video tracks`,
        redo: () => {
          if (!kernel.current) return;
          selectedLayerIds.forEach((layerId) => {
            kernel.current?.setLayer(layerId, (existingLayer) => ({
              ...existingLayer,
              visible: false,
              params: mergeTimelineAudioParams(existingLayer.params, { [TIMELINE_REPLACED_BY_EDIT_PARAM_KEY]: true }),
            }));
          });
          kernel.current.setLayer(mergedLayerId, (existingLayer) => ({
            ...existingLayer,
            ...(mergedLayerSnapshot ?? {}),
            type: 'video',
            visible: true,
            name: `${baseName} (Merged)`,
            imageSrc: undefined,
            imageData: null,
            videoSrc: uploadedVideoSrc,
            videoFramesUrls: [],
            timelineStartMs: Math.round(mergeRangeStartMs),
            timelineEndMs: Math.round(mergeRangeEndMs),
            timelineSourceInMs: 0,
            params: mergeTimelineAudioParams(existingLayer.params, {
              [TIMELINE_AUDIO_ONLY_PARAM_KEY]: false,
              [TIMELINE_MUTE_AUDIO_PARAM_KEY]: false,
              [TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY]: null,
              [TIMELINE_REPLACED_BY_EDIT_PARAM_KEY]: null,
            }),
            pulseToken: Date.now(),
          }));
          kernel.current.setWorkspace(workspaceId, (existingWorkspace) => {
            const currentLayerIds = Array.isArray(existingWorkspace.layerIds)
              ? existingWorkspace.layerIds.filter((layerId): layerId is string => typeof layerId === 'string')
              : [];
            const withoutMergedLayer = currentLayerIds.filter((layerId) => layerId !== mergedLayerId);
            const clampedInsertionIndex = clamp(insertionIndex, 0, withoutMergedLayer.length);
            const nextLayerIds = [
              ...withoutMergedLayer.slice(0, clampedInsertionIndex),
              mergedLayerId,
              ...withoutMergedLayer.slice(clampedInsertionIndex),
            ];
            nextLayerIds.forEach((layerId, index) => {
              kernel.current?.setLayer(layerId, (existingLayer) => ({
                ...existingLayer,
                renderOrder: index,
              }));
            });
            return {
              ...existingWorkspace,
              layerIds: nextLayerIds,
            };
          }, projectId);
        },
        undo: () => {
          if (!kernel.current) return;
          Object.entries(selectedVisibilityBefore).forEach(([layerId, wasVisible]) => {
            kernel.current?.setLayer(layerId, (existingLayer) => ({
              ...existingLayer,
              visible: wasVisible,
              params: mergeTimelineAudioParams(existingLayer.params, { [TIMELINE_REPLACED_BY_EDIT_PARAM_KEY]: null }),
            }));
          });
          const restoredMergedLayer = cloneTimelineValue(mergedLayerSnapshot);
          kernel.current.setLayer(mergedLayerId, (existingLayer) => ({
            ...existingLayer,
            ...(restoredMergedLayer ?? {}),
            visible: false,
            params: mergeTimelineAudioParams(restoredMergedLayer?.params ?? existingLayer.params, { [TIMELINE_REPLACED_BY_EDIT_PARAM_KEY]: true }),
            pulseToken: Date.now() + 1,
          }));
          kernel.current.setWorkspace(workspaceId, (existingWorkspace) => {
            const nextLayerIds = [...workspaceLayerIdsBefore];
            nextLayerIds.forEach((layerId, index) => {
              kernel.current?.setLayer(layerId, (existingLayer) => ({
                ...existingLayer,
                renderOrder: index,
              }));
            });
            return {
              ...existingWorkspace,
              layerIds: nextLayerIds,
            };
          }, projectId);
        },
      });
      toast.success('Video tracks merged into a new track.');
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        kernel.current?.handleError(error?.message || 'Failed to merge selected video tracks.');
      }
    } finally {
      mergeAbortControllersRef.current.delete(mergeAbortController);
      mergingLayerIds.forEach((layerId) => mergingTrackLockRef.current.delete(layerId));
      if (isMountedRef.current) {
        setMergingTrackIds((previous) => previous.filter((layerId) => !mergingLayerIdSet.has(layerId)));
      }
    }
  }, [isVideoTimelineAudioMuted, kernel, projectId, selectedVideoTracksForMerge, workspaceFrameH, workspaceFrameW, workspaceId, workspaceTimelineFps]);

  const {
    handleTrackTimelineToolContextMenu,
    handlePendingTrackContextMenu,
  } = useWorkspaceTimelineContextMenuController({
    kernel,
    projectId,
    workspaceId,
    resolvedTracks,
    separatingSoundTrackIdSet,
    mergingTrackIdSet,
    pendingVideoGenerationTracks,
    clampedCursorMsRef,
    volumeEditorTrackIdRef,
    seekPlaybackCursor,
    setSplitPreview,
    splitTrackAtMs,
    separateTrackAudio,
    mergeSelectedVideoTracks,
    openVolumeEditorForTrack,
    closeVolumeEditor,
  });

  const isImportingAssets = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.runtime?.isImportingAssets === true
  );
  // A project opens fitted before this dock exists, so the frame lands under it: refit once while the
  // project is opening, so a later workspace or view switch keeps the user's zoom.
  // shortcut: "opening" is the first seconds after mount; upgrade when the open fit itself knows the dock.
  const isDockExpanded = isActive && !showVersionView;
  const mountedAtRef = useRef<number | null>(null);
  const hasFittedAboveDockRef = useRef(false);
  useEffect(() => {
    mountedAtRef.current = performance.now();
  }, []);
  useEffect(() => {
    if (!isDockExpanded || hasFittedAboveDockRef.current) return;
    if (mountedAtRef.current === null || performance.now() - mountedAtRef.current > DOCK_OPEN_FIT_WINDOW_MS) return;
    const frame = requestAnimationFrame(() => {
      hasFittedAboveDockRef.current = true;
      const frameRect = kernel.current?.getWorkspace(workspaceId)?.frameRect;
      if (frameRect) kernel.current?.zoomToFitRectInViewport(projectId, frameRect, 1.2, kernel.current.getSpectacleMode());
    });
    return () => cancelAnimationFrame(frame);
  }, [isDockExpanded, projectId, workspaceId]);
  const isTimelineEmpty = !hasAnyTrack && pendingVideoGenerationTracks.length === 0 && !isImportingAssets;
  const dockStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_DOCK_STYLE,
    ...(isTimelineEmpty ? TIMELINE_DOCK_EMPTY_STYLE : TIMELINE_DOCK_FILLED_STYLE),
    ...(isDockExpanded ? TIMELINE_DOCK_OPEN_STYLE : TIMELINE_DOCK_CLOSED_STYLE),
    display: isActive ? 'flex' : 'none',
  }), [isActive, isDockExpanded, isTimelineEmpty]);

  const activeVideoInputTimelineRange = useMemo(() => {
    if (!VIDEO_INPUT_TIMELINE_MODELS.has(diffusionModel)) return null;
    const params = activeModelParams && typeof activeModelParams === 'object' && !Array.isArray(activeModelParams)
      ? activeModelParams as unknown as Record<string, unknown>
      : null;
    if (!params) return null;
    if (
      (diffusionModel === 'kling-3-omni' || diffusionModel === 'seedance-2.0' || diffusionModel === 'seedance-2.5' || diffusionModel === 'gemini-omni-flash-preview') &&
      params.reference_input_mode !== 'video'
    ) {
      return null;
    }
    const startRaw = toFinite(params.video_input_start_ms);
    const endRaw = toFinite(params.video_input_end_ms);
    const hasExplicitStart = startRaw !== null;
    const hasExplicitEnd = endRaw !== null;
    if (!hasExplicitStart && !hasExplicitEnd) return null;
    const startMs = Math.max(0, Math.round(startRaw ?? 0));
    const endMs = Math.max(0, Math.round(endRaw ?? rulerRangeEndMs));
    if (endMs <= startMs) return null;
    return {
      id: 'active-video-input-range',
      startMs,
      endMs,
      label: 'video input range',
    };
  }, [activeModelParams, diffusionModel, rulerRangeEndMs]);

  const pendingVideoInputTimelineRanges = useMemo(() => (
    generationTasks
      .filter((task) => task.workspaceId === workspaceId && VIDEO_INPUT_TIMELINE_MODELS.has(task.model))
      .map((task) => {
        const params = task.params && typeof task.params === 'object' && !Array.isArray(task.params)
          ? task.params as unknown as Record<string, unknown>
          : null;
        if (!params) return null;
        if (
          (task.model === 'kling-3-omni' || task.model === 'seedance-2.0' || task.model === 'seedance-2.5' || task.model === 'gemini-omni-flash-preview') &&
          params.reference_input_mode !== 'video'
        ) {
          return null;
        }
        const hasExplicitRange =
          toFinite(params.video_input_start_ms) !== null ||
          toFinite(params.video_input_end_ms) !== null;
        if (!hasExplicitRange) return null;
        const layerOverride = task.layerOverride && typeof task.layerOverride === 'object'
          ? task.layerOverride as Record<string, unknown>
          : null;
        const startMs = Math.max(0, Math.round(
          toFinite(layerOverride?.timelineStartMs) ??
          toFinite(params.video_input_start_ms) ??
          0
        ));
        const endMs = Math.max(0, Math.round(
          toFinite(layerOverride?.timelineEndMs) ??
          toFinite(params.video_input_end_ms) ??
          rulerRangeEndMs
        ));
        if (endMs <= startMs) return null;
        return {
          id: `pending-video-input-range-${task.id}`,
          startMs,
          endMs,
          label: 'processing video input range',
        };
      })
      .filter((range): range is { id: string; startMs: number; endMs: number; label: string } => Boolean(range))
  ), [generationTasks, rulerRangeEndMs, workspaceId]);

  const videoInputTimelineRanges = useMemo(() => {
    const ranges = [
      ...(activeVideoInputTimelineRange ? [activeVideoInputTimelineRange] : []),
      ...pendingVideoInputTimelineRanges,
    ];
    return ranges.sort((left, right) => {
      if (left.startMs !== right.startMs) return left.startMs - right.startMs;
      if (left.endMs !== right.endMs) return left.endMs - right.endMs;
      return left.id.localeCompare(right.id);
    });
  }, [activeVideoInputTimelineRange, pendingVideoInputTimelineRanges]);

  const videoInputTimelineRangeSignature = useMemo(() => (
    videoInputTimelineRanges
      .map((range) => `${range.id}:${Math.round(range.startMs)}:${Math.round(range.endMs)}`)
      .join('|')
  ), [videoInputTimelineRanges]);

  const timelineRenderRangeStartMs = useMemo(() => (
    videoInputTimelineRanges.reduce(
      (min, range) => Math.min(min, range.startMs),
      baseTimelineRenderRangeStartMs
    )
  ), [baseTimelineRenderRangeStartMs, videoInputTimelineRanges]);

  const timelineRenderRangeEndMs = useMemo(() => (
    Math.max(
      videoInputTimelineRanges.reduce(
        (max, range) => Math.max(max, range.endMs),
        baseTimelineRenderRangeEndMs
      ),
      timelineRenderRangeStartMs + 1
    )
  ), [baseTimelineRenderRangeEndMs, timelineRenderRangeStartMs, videoInputTimelineRanges]);

  const timelineRenderRangeMs = Math.max(1, timelineRenderRangeEndMs - timelineRenderRangeStartMs);

  const cursorPercent = useMemo(() => {
    if (timelineRenderRangeMs <= 0) return 0;
    return ((clampedCursorMs - timelineRenderRangeStartMs) / timelineRenderRangeMs) * 100;
  }, [clampedCursorMs, timelineRenderRangeMs, timelineRenderRangeStartMs]);

  const dragSnapGuidePercent = useMemo(() => {
    if (dragSnapGuideMs === null || timelineRenderRangeMs <= 0) return null;
    const clampedGuideMs = clamp(dragSnapGuideMs, timelineRenderRangeStartMs, timelineRenderRangeEndMs);
    return clamp(((clampedGuideMs - timelineRenderRangeStartMs) / timelineRenderRangeMs) * 100, 0, 100);
  }, [dragSnapGuideMs, timelineRenderRangeEndMs, timelineRenderRangeMs, timelineRenderRangeStartMs]);

  const stopTimelineControlPointerStart = useCallback((
    event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>
  ) => {
    event.stopPropagation();
  }, []);

  const rulerTotalSeconds = useMemo(
    () => Math.max(1, Math.ceil(timelineRenderRangeMs / 1000)),
    [timelineRenderRangeMs]
  );
  const rulerStepSeconds = useMemo(() => {
    if (rulerTotalSeconds <= 12) return 1;
    if (rulerTotalSeconds <= 30) return 2;
    if (rulerTotalSeconds <= 90) return 5;
    if (rulerTotalSeconds <= 180) return 10;
    if (rulerTotalSeconds <= 600) return 30;
    if (rulerTotalSeconds <= 1800) return 60;
    return 120;
  }, [rulerTotalSeconds]);
  const rulerTickSeconds = useMemo(() => {
    const ticks: number[] = [];
    for (let second = 0; second <= rulerTotalSeconds; second += rulerStepSeconds) {
      ticks.push(second);
    }
    const lastTick = ticks[ticks.length - 1] ?? 0;
    if (lastTick !== rulerTotalSeconds) {
      ticks.push(rulerTotalSeconds);
    }
    return ticks;
  }, [rulerStepSeconds, rulerTotalSeconds]);

  const isSplitToolActive = activeTool === 'scissors';
  const isSoundSeparationToolActive = activeTool === 'sound' && activeSoundTool === 'separation';
  const isSoundVolumeToolActive = activeTool === 'sound' && activeSoundTool === 'volume';
  const isSoundToolActive = activeTool === 'sound';
  const canReorderTrackOrder = displayTrackOrderLayerIds.length > 1 && !isSplitToolActive && !isSoundToolActive;

  const handleToggleTrackVisibility = useCallback((layerId: string) => {
    kernel.current?.updateLayerVisibility(layerId);
    // Hidden by hand, a layer stays a lane the user can show again, whatever an earlier edit marked.
    if (readTimelineParamFlag(kernel.current?.getLayerRegistry()[layerId]?.params, TIMELINE_REPLACED_BY_EDIT_PARAM_KEY)) {
      kernel.current?.setLayer(layerId, (layer) => ({
        ...layer,
        params: mergeTimelineAudioParams(layer.params, { [TIMELINE_REPLACED_BY_EDIT_PARAM_KEY]: null }),
      }));
    }
  }, [kernel]);

  const timelineRangePresentation = useMemo<TimelineRangePresentation>(() => ({
    startMs: timelineRenderRangeStartMs,
    endMs: timelineRenderRangeEndMs,
    rangeMs: timelineRenderRangeMs,
    videoInputTimelineRanges,
    videoInputTimelineRangeSignature,
  }), [
    timelineRenderRangeEndMs,
    timelineRenderRangeMs,
    timelineRenderRangeStartMs,
    videoInputTimelineRangeSignature,
    videoInputTimelineRanges,
  ]);
  const timelineToolState = useMemo<TimelineToolState>(() => ({
    isSplitToolActive,
    isSoundSeparationToolActive,
    isSoundVolumeToolActive,
    isSoundToolActive,
    canReorderTrackOrder,
  }), [
    canReorderTrackOrder,
    isSoundSeparationToolActive,
    isSoundToolActive,
    isSoundVolumeToolActive,
    isSplitToolActive,
  ]);
  const timelineLaneActions = useMemo<TimelineLaneRowActions>(() => ({
    onToggleTrackVisibility: handleToggleTrackVisibility,
    onRegisterLaneRef: registerTrackLaneRef,
    onStartTrackOrderDrag: startTrackOrderDrag,
    onStartTrackDrag: startTrackDrag,
    onTrackTimelineToolMouseDown: handleTrackTimelineToolMouseDown,
    onTrackTimelineToolContextMenu: handleTrackTimelineToolContextMenu,
    onResolveTrackFromLanePointer: resolveTrackFromLanePointer,
    onResolveSplitMsFromLanePointer: resolveSplitMsFromLanePointer,
    onSetSplitHoverTrackId: setSplitHoverTrackId,
    onSetSplitPreview: setSplitPreview,
  }), [
    handleToggleTrackVisibility,
    handleTrackTimelineToolContextMenu,
    handleTrackTimelineToolMouseDown,
    registerTrackLaneRef,
    resolveSplitMsFromLanePointer,
    resolveTrackFromLanePointer,
    startTrackDrag,
    startTrackOrderDrag,
  ]);
  const timelineLanePresentationRowsWithVolume = useMemo(() => {
    if (!volumeOverlayTrack) return timelineLanePresentationRows;
    return timelineLanePresentationRows.map((row) => {
      const rowVolumeOverlayTrack = row.laneLayerIds.includes(volumeOverlayTrack.layerId)
        ? volumeOverlayTrack
        : null;
      if (row.volumeOverlayTrack === rowVolumeOverlayTrack) return row;
      return {
        ...row,
        volumeOverlayTrack: rowVolumeOverlayTrack,
      };
    });
  }, [timelineLanePresentationRows, volumeOverlayTrack]);

  const shouldRenderEndInsertLane =
    (laneRows.length === 0 && pendingVideoGenerationTracks.length === 0) ||
    timelineFileDropPreview?.insertAtEnd === true ||
    (trackLaneDragPreview?.mode === 'insert' && trackLaneDragPreview.insertAtEnd) ||
    showEndInsertLaneWhileDragging;
  const shouldShowEndInsertLaneFileDropPreview = Boolean(
    timelineFileDropPreview && (laneRows.length === 0 || timelineFileDropPreview.insertAtEnd)
  );
  const panelContent = (
    <div style={TIMELINE_PORTAL_ROOT_STYLE}>
      <div
        style={dockStyle}
        onMouseDown={stopTimelineControlPointerStart}
        onTouchStart={stopTimelineControlPointerStart}
        data-timeline-dock={isDockExpanded ? 'open' : 'closed'}
        drop-area="timeline"
        onDragOver={handleTimelineDragOver}
        onDragEnter={handleTimelineDragOver}
        onDragLeave={handleTimelineDragLeave}
        onDrop={handleTimelineDrop}
      >
        {isTimelineEmpty ? (
          <div className="w-full">
              <WorkspaceTimelineInsertLanePreview
                shouldRender
                shouldShowFileDropPreview={Boolean(timelineFileDropPreview)}
                insertEndLaneRef={timelineInsertEndLaneRef}
                laneLeftColumnWidthPx={0}
                timelineFileDropPreview={timelineFileDropPreview}
                trackLaneDragPreview={trackLaneDragPreview}
                timelineRenderRangeStartMs={timelineRenderRangeStartMs}
                timelineRenderRangeMs={timelineRenderRangeMs}
                timelineDropPlaceholderDurationMs={timelineDropPlaceholderDurationMs}
              />
          </div>
        ) : (
          <div className="relative flex min-h-0 w-full flex-col">
            <WorkspaceTimelinePlaybackControls
              laneLeftColumnWidthPx={LANE_LEFT_COLUMN_WIDTH_PX}
              workspaceId={workspaceId}
              hasAnyTrack={hasAnyTrack}
              isAtTimelineEnd={isAtTimelineEnd}
              isComposedPreparing={isComposedPreparing}
              primaryControlLabel={primaryControlLabel}
              playbackRateLabel={playbackRateLabel}
              clampedCursorMs={clampedCursorMs}
              totalMs={durationMs}
              onPrimaryAction={handlePrimaryAction}
              onStepBackward={handleStepBackward}
              onStepForward={handleStepForward}
              onToggleLoop={handleToggleLoop}
              onToggleMuted={handleToggleMuted}
              onOpenPlaybackRateMenu={handleOpenPlaybackRateMenu}
            />
            <div
              className="grid w-full items-center"
              style={TIMELINE_RULER_GRID_STYLE}
            >
              <div />
              <WorkspaceTimelineRuler
                rulerRef={rulerRef}
                rulerTickSeconds={rulerTickSeconds}
                rulerStepSeconds={rulerStepSeconds}
                timelineRenderRangeStartMs={timelineRenderRangeStartMs}
                timelineRenderRangeEndMs={timelineRenderRangeEndMs}
                timelineRenderRangeMs={timelineRenderRangeMs}
                clampedCursorMs={clampedCursorMs}
                cursorPercent={cursorPercent}
                dragSnapGuidePercent={dragSnapGuidePercent}
                onMouseDown={handleRulerMouseDown}
              />
            </div>

            <div className="relative flex w-full flex-col gap-1" style={TIMELINE_LANES_SCROLL_STYLE}>
              <WorkspaceTimelineInsertLanePreview
                shouldRender={shouldRenderEndInsertLane}
                shouldShowFileDropPreview={shouldShowEndInsertLaneFileDropPreview}
                insertEndLaneRef={timelineInsertEndLaneRef}
                laneLeftColumnWidthPx={LANE_LEFT_COLUMN_WIDTH_PX}
                timelineFileDropPreview={timelineFileDropPreview}
                trackLaneDragPreview={trackLaneDragPreview}
                timelineRenderRangeStartMs={timelineRenderRangeStartMs}
                timelineRenderRangeMs={timelineRenderRangeMs}
                timelineDropPlaceholderDurationMs={timelineDropPlaceholderDurationMs}
              />
              {isImportingAssets && (
                <div className="flex items-center gap-1" style={TIMELINE_IMPORT_ROW_STYLE}>
                  <div className="flex w-5 justify-center"><SimpleLoader size={14} /></div>
                  <span style={TIMELINE_IMPORT_ROW_LABEL_STYLE}>Adding media</span>
                </div>
              )}
              <WorkspaceTimelinePendingTracks
                tracks={pendingVideoGenerationTracks}
                timelineRenderRangeStartMs={timelineRenderRangeStartMs}
                timelineRenderRangeMs={timelineRenderRangeMs}
                laneLeftColumnWidthPx={LANE_LEFT_COLUMN_WIDTH_PX}
                onPendingTrackContextMenu={handlePendingTrackContextMenu}
              />
              <WorkspaceTimelineLaneList
                rows={timelineLanePresentationRowsWithVolume}
                range={timelineRangePresentation}
                layout={TIMELINE_LANE_LAYOUT_PRESENTATION}
                tools={timelineToolState}
                actions={timelineLaneActions}
                volumeActions={timelineVolumeActions}
              />
            </div>
            <WorkspaceTimelineCursorOverlay
              cursorPercent={cursorPercent}
              dragSnapGuidePercent={dragSnapGuidePercent}
              laneLeftColumnWidthPx={LANE_LEFT_COLUMN_WIDTH_PX}
              timelineGridGapPx={TIMELINE_GRID_GAP_PX}
            />
          </div>
        )}
      </div>
    </div>
  );

  const portalHost = kernel.current?.canvasContainerRef.current ?? null;
  const panelPortal = portalHost ? createPortal(panelContent, portalHost) : panelContent;
  return panelPortal;
});

WorkspaceTimelinePanel.displayName = 'WorkspaceTimelinePanel';

interface WorkspaceTimelineMountGateProps extends WorkspaceTimelinePanelProps {
  children?: (state: {
    shouldRenderVersionsPanel: boolean;
    timelineMountSignature: string;
  }) => React.ReactNode;
}

export const WorkspaceTimelineMountGate = React.memo(({
  projectId,
  workspaceId,
  isActive,
  composedPlaybackVideoRef,
  playbackMediaClockHintRef,
  onComposedPlaybackStateChange,
  children,
}: WorkspaceTimelineMountGateProps) => {
  const timelineMountSignature = useKernelStore((state) => (
    buildWorkspaceTimelineSummarySignature({
      workspaceId,
      workspace: state.inventory.StudioWorkspace.registry[workspaceId] ?? null,
      getLayer: (layerId) => state.inventory.StudioLayer['registry'][layerId],
    })
  ));

  const [
    hasTimelineTracksRaw,
    ,
    hasInitializedImageTimelineRaw,
    ,
    preferVersionViewRaw,
    imageTimelineManuallyEnabledRaw,
  ] = timelineMountSignature.split('|');

  const shouldRenderTimelinePanel =
    hasTimelineTracksRaw === '1' ||
    isActive ||
    hasInitializedImageTimelineRaw === '1' ||
    imageTimelineManuallyEnabledRaw === '1';
  const shouldRenderVersionsPanel = preferVersionViewRaw !== '0';

  return (
    <>
      {shouldRenderTimelinePanel && (
        <WorkspaceTimelinePanel
          projectId={projectId}
          workspaceId={workspaceId}
          isActive={isActive}
          composedPlaybackVideoRef={composedPlaybackVideoRef}
          playbackMediaClockHintRef={playbackMediaClockHintRef}
          onComposedPlaybackStateChange={onComposedPlaybackStateChange}
        />
      )}
      {children?.({ shouldRenderVersionsPanel, timelineMountSignature })}
    </>
  );
});

WorkspaceTimelineMountGate.displayName = 'WorkspaceTimelineMountGate';
